import React from 'react';
import { ArrowRight } from 'lucide-react';

const ValidatedTechSection = () => {
  return (
    <section id="validated-tech" className="relative py-24 bg-gray-900 overflow-hidden" data-testid="validated-tech-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-4">
            <h2 className="text-3xl md:text-4xl font-bold text-white">Validated Tech</h2>
            <div className="h-1 w-16 bg-gradient-to-r from-emerald-500 to-cyan-500 rounded-full" />
          </div>
        </div>

        {/* Cycle Life Raw Data */}
        <div className="bg-gray-950 rounded-2xl border border-white/10 p-8 lg:p-12">
          <h3 className="text-xl md:text-2xl font-bold text-white mb-4 tracking-tight">
            CYCLE LIFE RAW DATA
          </h3>
          <p className="text-gray-400 mb-8 max-w-3xl leading-relaxed">
            Our in-house Cell and Pack testing facility has validated our technology over multiple 
            temperature conditions. Our Cells have also undergone rigorous abuse testing and has 
            passed all internal QA processes.
          </p>

          {/* Graph Container */}
          <div className="bg-white rounded-xl p-6 mb-6">
            <svg viewBox="0 0 800 400" className="w-full h-auto">
              <defs>
                <linearGradient id="graphLine" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#DC2626" />
                  <stop offset="100%" stopColor="#DC2626" />
                </linearGradient>
              </defs>

              {/* Grid lines */}
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <g key={i}>
                  <line 
                    x1="80" 
                    y1={60 + i * 60} 
                    x2="750" 
                    y2={60 + i * 60} 
                    stroke="#E5E7EB" 
                    strokeWidth="1" 
                  />
                  <text x="70" y={65 + i * 60} fontSize="12" fill="#6B7280" textAnchor="end">
                    {100 - i * 20}%
                  </text>
                </g>
              ))}

              {/* Vertical grid lines */}
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <g key={`v-${i}`}>
                  <line 
                    x1={80 + i * 134} 
                    y1="60" 
                    x2={80 + i * 134} 
                    y2="360" 
                    stroke="#E5E7EB" 
                    strokeWidth="1" 
                  />
                  <text x={80 + i * 134} y="380" fontSize="12" fill="#6B7280" textAnchor="middle">
                    {i * 1000}
                  </text>
                </g>
              ))}

              {/* Y-axis label */}
              <text 
                x="25" 
                y="210" 
                fontSize="12" 
                fill="#6B7280" 
                textAnchor="middle"
                transform="rotate(-90, 25, 210)"
              >
                %Capacity retention
              </text>

              {/* X-axis label */}
              <text x="415" y="400" fontSize="12" fill="#6B7280" textAnchor="middle">
                Cycle ID
              </text>

              {/* Main data line - realistic battery degradation curve with noise */}
              <path
                d={`
                  M 80,68 
                  Q 85,67 90,69 Q 95,68 100,70 Q 108,69 115,71
                  Q 125,70 135,72 Q 145,71 155,73 Q 165,72 175,74
                  Q 190,73 205,76 Q 220,75 235,78 Q 250,77 265,80
                  Q 285,79 305,83 Q 325,82 345,86 Q 365,85 385,89
                  Q 405,88 425,92 Q 445,91 465,95 Q 485,94 505,98
                  Q 520,97 535,100 Q 550,99 565,102 Q 580,101 595,104
                  Q 608,103 620,106 Q 632,105 645,107 Q 658,106 670,108
                  Q 680,108 690,110 Q 700,109 710,111 Q 720,111 730,113
                  Q 740,112 750,114
                `}
                fill="none"
                stroke="#DC2626"
                strokeWidth="2"
              />

              {/* Secondary noise layer for more realistic look */}
              <path
                d={`
                  M 80,66
                  L 95,70 L 110,68 L 125,72 L 140,70 L 155,74 L 170,72 L 185,76
                  L 200,74 L 215,78 L 230,76 L 245,80 L 260,78 L 275,82 L 290,80
                  L 305,84 L 320,82 L 335,87 L 350,84 L 365,89 L 380,86 L 395,91
                  L 410,88 L 425,93 L 440,90 L 455,95 L 470,92 L 485,97 L 500,94
                  L 515,99 L 530,96 L 545,101 L 560,98 L 575,103 L 590,100 L 605,105
                  L 620,102 L 635,107 L 650,104 L 665,108 L 680,106 L 695,110
                  L 710,108 L 725,112 L 740,110 L 750,115
                `}
                fill="none"
                stroke="#DC2626"
                strokeWidth="1.5"
                opacity="0.7"
              />

              {/* Scatter points for realistic data appearance */}
              {[
                [90, 68], [120, 71], [150, 73], [180, 75], [210, 77], [240, 79],
                [270, 81], [300, 84], [330, 86], [360, 88], [390, 91], [420, 93],
                [450, 96], [480, 98], [510, 100], [540, 102], [570, 104], [600, 106],
                [630, 108], [660, 109], [690, 111], [720, 113], [750, 114]
              ].map(([x, y], i) => (
                <circle 
                  key={i} 
                  cx={x} 
                  cy={y + (Math.sin(i * 1.5) * 2)} 
                  r="2" 
                  fill="#DC2626" 
                  opacity="0.6"
                />
              ))}

              {/* 80% threshold line */}
              <line 
                x1="80" 
                y1="120" 
                x2="750" 
                y2="120" 
                stroke="#10B981" 
                strokeWidth="2" 
                strokeDasharray="8,4"
                opacity="0.5"
              />
              <text x="755" y="124" fontSize="11" fill="#10B981" fontWeight="600">80%</text>

              {/* Charge/Discharge Rate Box */}
              <g transform="translate(580, 280)">
                <text fontSize="14" fill="#6B7280">Charge Rate</text>
                <text x="120" fontSize="14" fill="#1F2937" fontWeight="bold">4C</text>
                <text y="25" fontSize="14" fill="#6B7280">Discharge Rate</text>
                <text x="120" y="25" fontSize="14" fill="#1F2937" fontWeight="bold">4C</text>
              </g>
            </svg>
          </div>

          {/* Caption */}
          <p className="text-gray-400 text-sm">
            Internal cycler data of Gegadyne Energy's cell showing{' '}
            <span className="text-emerald-400 font-semibold">retention &gt; 80% at ~5000 cycles</span>
          </p>

          {/* Key Highlights */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-10 pt-10 border-t border-white/10">
            <div className="text-center">
              <p className="text-4xl font-bold text-white mb-2">5,000+</p>
              <p className="text-gray-400 text-sm">Validated Cycles</p>
            </div>
            <div className="text-center">
              <p className="text-4xl font-bold text-emerald-400 mb-2">&gt;80%</p>
              <p className="text-gray-400 text-sm">Capacity Retention</p>
            </div>
            <div className="text-center">
              <p className="text-4xl font-bold text-white mb-2">4C/4C</p>
              <p className="text-gray-400 text-sm">Charge/Discharge Rate</p>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-8 text-center">
          <a
            href="#contact"
            className="inline-flex items-center gap-2 text-emerald-400 hover:text-emerald-300 font-semibold transition-colors"
          >
            Request detailed test reports
            <ArrowRight className="w-5 h-5" />
          </a>
        </div>
      </div>
    </section>
  );
};

export default ValidatedTechSection;
